import { trackEvent, AnalyticsEvents } from '../../analytics';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Grid,
  Stack,
  Textarea,
  Button,
  Group,
  Text,
  Paper,
  Badge,
  Box,
  ScrollArea,
  Center,
  Tooltip,
  Divider,
  ActionIcon,
  Menu,
} from '@mantine/core';
import {
  IconCopy,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCloud,
  IconCloudUpload,
  IconAlertCircle,
  IconSparkles,
  IconDotsVertical,
  IconEyeOff,
  IconUsers,
  IconSend,
} from '@tabler/icons-react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useGetPerformanceReviewRoundsRoundIdSubjectsUserIdFeedback,
  usePutPerformanceReviewRoundsRoundIdParticipantsUserIdSummaryBlocks,
  usePostPerformanceReviewRoundsRoundIdParticipantsUserIdSummaryPublish,
  getGetPerformanceReviewRoundsRoundIdParticipantsQueryKey,
  getGetPerformanceReviewRoundsRoundIdSummarizersMineQueryKey,
} from '../../api/generated/performance-review/performance-review';
import { useUsersDirectory } from '../../hooks/useUserApi';
import { PerformanceReviewQuestion } from '../../api/generated/model/performanceReviewQuestion';
import { User } from '../../types';
import { showToast } from '../../utils/toast';
import { api, getErrorMessage } from '../../utils/api';
import { perfReviewErrorMessage } from '../../pages/performance-review/utils/errorMessage';
import { CANNOT_ASSESS, resolveScaleLabels } from './QuestionField';
import { SubjectGoalsPanel } from '../../pages/performance-review/parts/SubjectGoalsPanel';
import { PerformanceReviewOutcomePanel, OutcomeValues } from './PerformanceReviewOutcomePanel';
import { scaleTone, scoreExpectation } from '../../pages/performance-review/parts/score';

interface FeedbackAnswer {
  questionId?: string;
  value?: unknown;
  respondent?: string;
  // Effective anonymity of this answer (question anonymous to peers AND not
  // signed via reveal-name). Admin still sees the author; this only flags how
  // it was submitted.
  anonymous?: boolean;
}

interface FeedbackEntry {
  respondent?: string;
  formId?: string;
  type?: 'peer' | 'manager';
  // Peer-question snapshot, stamped on every entry by the backend (same
  // value across entries within a (round, subject) — manager forms have
  // empty `questions` on the model itself, but the service backfills them
  // from the first non-empty peer snapshot before returning).
  questions?: PerformanceReviewQuestion[];
  answers?: FeedbackAnswer[];
}

interface SummaryBlockInput {
  block: string;
  comment: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  roundId: string;
  userId: string;
  // Block-based source of truth. Old free-text drafts kept around in legacy
  // rounds are migrated by saving them under a default-named block on first
  // open if needed; we don't surface them here.
  initialBlocks?: SummaryBlockInput[];
  // Already out to the subject — unlocks retraction and turns Publish into Republish.
  isPublished?: boolean;
  // Pre-published: the subject's lead/PM can read it, the subject cannot. Implied
  // by isPublished.
  isShared?: boolean;
  // Решение по грейду и встреча 1-on-1, уже записанные для участника.
  initialOutcome?: OutcomeValues;
}

const UNNAMED_BLOCK_KEY = '__unnamed__';

const formatAnswerText = (
  q: PerformanceReviewQuestion | undefined,
  value: unknown,
  // Resolved per-point labels (incl. the expectation-scale fallback) so scores
  // read as "3/5 — Соответствует ожиданиям" even when the question stores no
  // explicit scaleLabels.
  resolvedLabels?: string[],
): string => {
  if (value === CANNOT_ASSESS || value === null) return '(не могу оценить)';
  if (value === undefined || value === '') return '(не ответил)';
  if (!q) return String(value);
  switch (q.type) {
    case 'scale': {
      const num = Number(value);
      if (isNaN(num)) return String(value);
      const max = q.scaleMax ?? 5;
      const min = q.scaleMin ?? 1;
      const label = resolvedLabels?.[num - min];
      return `${num}/${max}${label ? ` — ${label}` : ''}`;
    }
    case 'multi-select':
      return Array.isArray(value) ? (value as string[]).join(', ') : String(value);
    default:
      return String(value);
  }
};

const renderAnswerValue = (
  q: PerformanceReviewQuestion | undefined,
  value: unknown,
  t: TFunction,
): React.ReactNode => {
  if (value === CANNOT_ASSESS || value === null) {
    return (
      <Badge variant="light" color="gray" size="sm">
        {t('perfReview.cannotAssess')}
      </Badge>
    );
  }
  if (value === undefined || value === '') {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        {t('perfReview.noAnswer')}
      </Text>
    );
  }
  if (!q) return <Text size="sm">{String(value)}</Text>;
  switch (q.type) {
    case 'scale': {
      const num = typeof value === 'number' ? value : Number(value);
      if (isNaN(num)) return <Text size="sm">{String(value)}</Text>;
      const max = q.scaleMax ?? 5;
      const min = q.scaleMin ?? 1;
      const count = max - min + 1;
      const defaults = t('perfReview.expectationScale', { returnObjects: true });
      const labels = resolveScaleLabels(
        q,
        Array.isArray(defaults) ? (defaults as string[]) : undefined,
      );
      const label = labels?.[num - min];
      const tone = scaleTone(num - min + 1, count);
      return (
        <Group gap="xs" wrap="nowrap" align="center">
          <Badge
            variant="light"
            color={tone}
            size="sm"
            radius="sm"
            style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
          >
            {num}/{max}
          </Badge>
          {label && (
            <Text size="sm" fw={500}>
              {label}
            </Text>
          )}
        </Group>
      );
    }
    case 'multi-select':
      return (
        <Text size="sm">
          {Array.isArray(value) ? (value as string[]).join(', ') : String(value)}
        </Text>
      );
    case 'select':
    case 'text':
    default:
      return (
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {String(value)}
        </Text>
      );
  }
};

export const PerformanceReviewSummaryEditModal: React.FC<Props> = ({
  opened,
  onClose,
  roundId,
  userId,
  initialBlocks,
  isPublished = false,
  initialOutcome,
  isShared = false,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Оба списка держат блоки саммари: админская таблица участников и личный
  // список ответственного. Освежать надо оба — личная страница отдаёт блоки в
  // `initialBlocks`, и на устаревшем снимке редактор открылся бы пустым, а
  // первое же сохранение затёрло бы написанное.
  const invalidateSummaryQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getGetPerformanceReviewRoundsRoundIdParticipantsQueryKey(roundId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetPerformanceReviewRoundsRoundIdSummarizersMineQueryKey(roundId),
    });
  };
  const { data: feedback, error: feedbackError } =
    useGetPerformanceReviewRoundsRoundIdSubjectsUserIdFeedback(roundId, userId, {
      query: { enabled: opened, retry: false },
    });
  // 409 subject_feedback_incomplete: сырые отзывы откроются, когда по человеку сдадут все.
  const feedbackLock = (
    feedbackError as {
      response?: { data?: { code?: string; details?: { submitted?: number; total?: number } } };
    } | null
  )?.response?.data;
  const lockedText =
    feedbackLock?.code === 'subject_feedback_incomplete'
      ? t('perfReview.feedbackLocked', {
          submitted: feedbackLock.details?.submitted ?? 0,
          total: feedbackLock.details?.total ?? 0,
        })
      : null;
  const { data: users = [] } = useUsersDirectory();
  const saveBlocks = usePutPerformanceReviewRoundsRoundIdParticipantsUserIdSummaryBlocks();
  const publish = usePostPerformanceReviewRoundsRoundIdParticipantsUserIdSummaryPublish();
  const aiDraft = useMutation({
    mutationFn: async (): Promise<{ blocks: { block: string; comment: string }[] }> =>
      api(`/performance-review/rounds/${roundId}/participants/${userId}/summary/ai-draft`, {
        method: 'POST',
      }),
  });
  const unpublish = useMutation({
    mutationFn: async (): Promise<unknown> =>
      api(`/performance-review/rounds/${roundId}/participants/${userId}/summary/publish`, {
        method: 'DELETE',
      }),
  });
  // Pre-publication: lead/PM only. Not in the generated client yet, same as the
  // two above — hand-rolled rather than regenerating orval for two endpoints.
  const share = useMutation({
    mutationFn: async (blocks: { block: string; comment: string }[]): Promise<unknown> =>
      api(`/performance-review/rounds/${roundId}/participants/${userId}/summary/share`, {
        method: 'POST',
        // `api` stringifies the body itself — passing a string here would double-encode it.
        body: { blocks },
      }),
  });
  const unshare = useMutation({
    mutationFn: async (): Promise<unknown> =>
      api(`/performance-review/rounds/${roundId}/participants/${userId}/summary/share`, {
        method: 'DELETE',
      }),
  });

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of (users as User[]) || []) map[u._id] = u.name;
    return map;
  }, [users]);

  const subject = useMemo(
    () => ((users as User[]) || []).find((u) => u._id === userId),
    [users, userId],
  );
  const subjectName = subject?.name || userNameById[userId] || '';

  // Peer questions are snapshotted on each form (built from the subject's
  // competency-map swimlanes at assignment time). The backend stamps the
  // same shared snapshot on every entry, but we still scan defensively in
  // case some entry came back with `questions: []` — never want to silently
  // collapse to an empty editor again.
  const allQuestions = useMemo(() => {
    const entries = (feedback as unknown as FeedbackEntry[]) || [];
    return entries.find((e) => e.questions && e.questions.length > 0)?.questions || [];
  }, [feedback]);

  const questionMap = useMemo(() => {
    const map: Record<string, PerformanceReviewQuestion> = {};
    for (const qq of allQuestions) {
      if (qq.id) map[qq.id] = qq;
    }
    return map;
  }, [allQuestions]);

  // Block ordering follows the order they appear in the questionnaire.
  const blockOrder = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const qq of allQuestions) {
      const key = qq.block?.trim() || UNNAMED_BLOCK_KEY;
      if (!seen.has(key)) {
        seen.add(key);
        list.push(key);
      }
    }
    return list;
  }, [allQuestions]);

  const blockTitle = (key: string) =>
    key === UNNAMED_BLOCK_KEY ? t('perfReview.unnamedSection') : key;

  const entries = (feedback as unknown as FeedbackEntry[]) || [];

  // Group raw answers by block, then by question, with respondent + value.
  const answersByBlock = useMemo(() => {
    const out = new Map<
      string,
      {
        questionId: string;
        questionMeta?: PerformanceReviewQuestion;
        rows: { respondentLabel: string; value: unknown; anonymous?: boolean }[];
      }[]
    >();
    for (const key of blockOrder) out.set(key, []);
    const questionToBlockIndex = new Map<string, number>();
    allQuestions.forEach((qq) => {
      const key = qq.block?.trim() || UNNAMED_BLOCK_KEY;
      const list = out.get(key)!;
      const idx = list.length;
      list.push({ questionId: qq.id || '', questionMeta: qq, rows: [] });
      questionToBlockIndex.set(qq.id || '', idx);
    });
    entries.forEach((entry, i) => {
      const respondentLabel = entry.respondent
        ? userNameById[entry.respondent] || entry.respondent
        : t('perfReview.anonRespondent', { idx: i + 1 });
      (entry.answers || []).forEach((a) => {
        const qid = a.questionId ?? '';
        const meta = questionMap[qid];
        const key = meta?.block?.trim() || UNNAMED_BLOCK_KEY;
        const list = out.get(key);
        if (!list) {
          // Question whose block is unknown — drop into the unnamed bucket.
          const fallback = out.get(UNNAMED_BLOCK_KEY) ?? [];
          if (!out.has(UNNAMED_BLOCK_KEY)) out.set(UNNAMED_BLOCK_KEY, fallback);
          let row = fallback.find((r) => r.questionId === qid);
          if (!row) {
            row = { questionId: qid, questionMeta: undefined, rows: [] };
            fallback.push(row);
          }
          row.rows.push({ respondentLabel, value: a.value, anonymous: a.anonymous });
          return;
        }
        const row = list.find((r) => r.questionId === qid);
        if (row) row.rows.push({ respondentLabel, value: a.value, anonymous: a.anonymous });
      });
    });
    return out;
  }, [blockOrder, allQuestions, entries, questionMap, userNameById, t]);

  // Local edit state per block (key → comment).
  const [comments, setComments] = useState<Record<string, string>>({});
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);
  type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  // Tracks whether the user has touched comments since the last hydrate. We
  // refuse to overwrite their in-progress edits if `initialBlocks` arrives
  // later from a refetch (we'd otherwise wipe the textarea mid-paste).
  const isDirtyRef = useRef(false);
  const lastSavedJsonRef = useRef('');
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The (opened, userId) combination we last hydrated for. Lets us tell a
  // genuine subject switch / (re)open apart from a mere refetch that only
  // changed object identities — see the hydrate effect below.
  const hydratedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      // Force a fresh hydrate on the next open.
      hydratedForRef.current = null;
      return;
    }
    // A different subject (or a first open) must ALWAYS hydrate and reset dirty —
    // otherwise stale text from the previous subject would be saved under the new
    // userId. For the SAME subject, respect an in-progress edit: a refetch that
    // re-ran this effect (new blockOrder/initialBlocks identity) must not clobber
    // the textarea the admin is typing in.
    const switchedSubject = hydratedForRef.current !== userId;
    if (!switchedSubject && isDirtyRef.current) return;

    const init: Record<string, string> = {};
    for (const key of blockOrder) init[key] = '';
    for (const b of initialBlocks || []) {
      const key = b.block === UNNAMED_BLOCK_KEY ? UNNAMED_BLOCK_KEY : b.block;
      init[key] = b.comment;
    }
    setComments(init);
    lastSavedJsonRef.current = JSON.stringify(init);
    setAutosaveStatus('idle');
    isDirtyRef.current = false;
    hydratedForRef.current = userId;
  }, [opened, userId, blockOrder, initialBlocks]);

  const buildBlockText = (blockKey: string): string => {
    const defaults = t('perfReview.expectationScale', { returnObjects: true });
    const defaultLabels = Array.isArray(defaults) ? (defaults as string[]) : undefined;
    const lines: string[] = [];
    lines.push(`Subject: ${subjectName || userId}`);
    lines.push(`Block: ${blockTitle(blockKey)}`);
    lines.push('');
    const questions = answersByBlock.get(blockKey) || [];
    questions.forEach((qBlock) => {
      const qmeta = qBlock.questionMeta;
      // Scores aren't useful in the copied write-up — only the comments matter.
      if (qmeta?.type === 'scale') return;
      lines.push(`Q: ${qmeta?.text || qBlock.questionId}`);
      if (!qBlock.rows.length) {
        lines.push('  (нет ответов)');
      } else {
        const labels = resolveScaleLabels(qmeta ?? {}, defaultLabels);
        qBlock.rows.forEach((row) => {
          // Tag each author by how THIS answer was submitted — anonymously to
          // peers or signed. Per-answer (not per-question): a reviewer can sign
          // a comment while their rating stayed anonymous.
          const how = row.anonymous ? ' · анонимно' : ' · открыто';
          const who = `${row.respondentLabel || '(аноним)'}${how}`;
          lines.push(`  - [${who}] ${formatAnswerText(qmeta, row.value, labels)}`);
        });
      }
      lines.push('');
    });
    return lines.join('\n').trim();
  };

  const handleCopyBlock = async (blockKey: string) => {
    const text = buildBlockText(blockKey);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBlock(blockKey);
      setTimeout(() => setCopiedBlock((v) => (v === blockKey ? null : v)), 2000);
      showToast({ title: t('perfReview.copied'), message: blockTitle(blockKey), color: 'teal' });
    } catch {
      showToast({
        title: t('perfReview.copyFailedTitle'),
        message: t('perfReview.copyFailedBody'),
        color: 'red',
      });
    }
  };

  const blocksPayload = (snap: Record<string, string>): SummaryBlockInput[] =>
    blockOrder
      .filter((key) => (snap[key] ?? '').trim().length > 0)
      .map((key) => ({ block: key, comment: snap[key] ?? '' }));

  // Persist current comments and update the "last saved" snapshot. Used by
  // both autosave and manual Save.
  const persistDraft = async (snap: Record<string, string>) => {
    setAutosaveStatus('saving');
    try {
      await saveBlocks.mutateAsync({
        roundId,
        userId,
        data: { blocks: blocksPayload(snap) },
      });
      lastSavedJsonRef.current = JSON.stringify(snap);
      setAutosaveStatus('saved');
      // NOTE: do NOT clear isDirtyRef here. While the modal is open and the user
      // has been typing, their in-memory comments are the source of truth. The
      // invalidate below refetches participants, which re-runs the hydrate
      // effect; if we cleared the dirty flag, that effect would overwrite the
      // textarea with the (possibly stale) server snapshot mid-edit and wipe
      // the user's text. The flag is only reset on close/publish.
      invalidateSummaryQueries();
    } catch (e) {
      setAutosaveStatus('error');
      throw e;
    }
  };

  // Debounced autosave: 1.5s after last keystroke, only when the snapshot
  // really differs. Won't fire if the user opens the modal and doesn't edit.
  useEffect(() => {
    if (!opened) return;
    const json = JSON.stringify(comments);
    if (json === lastSavedJsonRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      persistDraft(comments).catch(() => {
        /* error already surfaced via setAutosaveStatus('error') */
      });
    }, 1500);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, opened]);

  // Best-effort flush of pending edits when the page is hidden or unloaded.
  useEffect(() => {
    if (!opened) return;
    const flush = () => {
      const json = JSON.stringify(comments);
      if (json === lastSavedJsonRef.current) return;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      persistDraft(comments).catch(() => {});
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, comments]);

  // AI draft: ask the model to summarize the feedback per block, then fill ONLY
  // the empty blocks so the admin's own text is never overwritten. Result is
  // staged into the textareas (marked dirty) — nothing is saved until the admin
  // reviews and clicks Save/Publish.
  const handleAiDraft = async () => {
    try {
      const { blocks } = await aiDraft.mutateAsync();
      if (!blocks?.length) {
        showToast({ title: t('perfReview.aiNoResult'), message: '', color: 'yellow' });
        return;
      }
      let filled = 0;
      // Only fill blocks that actually render a textarea (keys in blockOrder).
      // Guards against the model returning a slightly-altered block name that
      // wouldn't match any field — its text would otherwise vanish silently.
      const renderableBlocks = new Set(blockOrder);
      setComments((prev) => {
        const next = { ...prev };
        for (const b of blocks) {
          const key = b.block;
          if (!renderableBlocks.has(key)) continue;
          if ((next[key] ?? '').trim().length === 0 && b.comment.trim().length > 0) {
            next[key] = b.comment;
            filled += 1;
          }
        }
        return next;
      });
      isDirtyRef.current = true;
      showToast({
        title: t('perfReview.aiDraftDone'),
        message: t('perfReview.aiDraftFilled', { count: filled }),
        color: filled > 0 ? 'teal' : 'yellow',
      });
    } catch (e) {
      showToast({
        title: t('perfReview.aiDraftFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  const handleSave = async () => {
    try {
      await persistDraft(comments);
      showToast({ title: t('perfReview.draftSaved'), message: '', color: 'teal' });
    } catch (e) {
      showToast({
        title: t('perfReview.saveDraftFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  // Save+publish atomically via the publish endpoint's optional `blocks`
  // body — avoids the race where save succeeds, publish fails, and the user
  // sees only the publish error without knowing the draft is already persisted.
  const handlePublish = async () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    try {
      await publish.mutateAsync({
        roundId,
        userId,
        data: { blocks: blocksPayload(comments) },
      });
      trackEvent(AnalyticsEvents.REVIEW_SUMMARY_PUBLISHED);
      lastSavedJsonRef.current = JSON.stringify(comments);
      isDirtyRef.current = false;
      invalidateSummaryQueries();
      showToast({
        title: t('perfReview.summaryPublishedTitle'),
        message: t('perfReview.willGetEmail'),
        color: 'teal',
      });
      onClose();
    } catch (e) {
      showToast({
        title: t('perfReview.publishFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  // Pre-publication. Same save+act-atomically trick as handlePublish, but the
  // subject sees nothing and gets no email — only their lead/PM can read it.
  const handleShare = async () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    try {
      await share.mutateAsync(blocksPayload(comments));
      lastSavedJsonRef.current = JSON.stringify(comments);
      isDirtyRef.current = false;
      invalidateSummaryQueries();
      showToast({
        title: t('perfReview.summarySharedTitle'),
        message: t('perfReview.summarySharedMessage'),
        color: 'teal',
      });
      onClose();
    } catch (e) {
      showToast({
        title: t('perfReview.shareFailed'),
        message: perfReviewErrorMessage(t, e),
        color: 'red',
      });
    }
  };

  const handleUnshare = () => {
    modals.openConfirmModal({
      title: t('perfReview.unshareConfirmTitle'),
      centered: true,
      children: <Text size="sm">{t('perfReview.unshareConfirmBody')}</Text>,
      labels: {
        confirm: t('perfReview.unshareConfirmAccept'),
        cancel: t('perfReview.confirmCancel'),
      },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await unshare.mutateAsync();
          invalidateSummaryQueries();
          showToast({
            title: t('perfReview.unsharedTitle'),
            message: t('perfReview.unsharedMessage'),
            color: 'yellow',
          });
          onClose();
        } catch (e) {
          showToast({
            title: t('perfReview.unshareFailed'),
            message: perfReviewErrorMessage(t, e),
            color: 'red',
          });
        }
      },
    });
  };

  // Retraction hides the summary again and reopens it for editing. It cannot
  // unsend the notification mail or unread what the subject already read, so it
  // is worth one confirmation click.
  const handleUnpublish = () => {
    modals.openConfirmModal({
      title: t('perfReview.unpublishConfirmTitle'),
      centered: true,
      children: <Text size="sm">{t('perfReview.unpublishConfirmBody')}</Text>,
      labels: {
        confirm: t('perfReview.unpublishConfirmAccept'),
        cancel: t('perfReview.confirmCancel'),
      },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await unpublish.mutateAsync();
          invalidateSummaryQueries();
          showToast({
            title: t('perfReview.unpublishedTitle'),
            message: t('perfReview.unpublishedMessage'),
            color: 'yellow',
          });
          onClose();
        } catch (e) {
          showToast({
            title: t('perfReview.unpublishFailed'),
            message: perfReviewErrorMessage(t, e),
            color: 'red',
          });
        }
      },
    });
  };

  const handleClose = () => {
    if (isDirtyRef.current) {
      // Try to flush any pending edits so closing doesn't lose data even if
      // the autosave timer hasn't fired yet.
      persistDraft(comments).catch(() => {});
    }
    onClose();
  };

  const filledCount = blockOrder.filter((k) => (comments[k] ?? '').trim().length > 0).length;

  const autosaveBadge = (() => {
    if (autosaveStatus === 'saving') {
      return (
        <Badge variant="light" color="blue" leftSection={<IconCloudUpload size={12} />} size="sm">
          {t('perfReview.autosaveSaving')}
        </Badge>
      );
    }
    if (autosaveStatus === 'saved') {
      return (
        <Badge variant="light" color="teal" leftSection={<IconCloud size={12} />} size="sm">
          {t('perfReview.autosaveSaved')}
        </Badge>
      );
    }
    if (autosaveStatus === 'error') {
      return (
        <Badge variant="light" color="red" leftSection={<IconAlertCircle size={12} />} size="sm">
          {t('perfReview.autosaveError')}
        </Badge>
      );
    }
    return null;
  })();

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="90%"
      title={t('perfReview.summaryEditor')}
      // Protect long-form pasted summaries — clicks outside / Esc shouldn't
      // discard work; user must click Close (which still flushes via handleClose).
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group justify="space-between" mb="sm">
            <Text fw={600}>{t('perfReview.rawFeedbackByBlock')}</Text>
            <Badge variant="light" color="blue">
              {t('perfReview.rawFeedback', { count: entries.length })}
            </Badge>
          </Group>
          <ScrollArea h="70vh" type="auto">
            <Stack gap="md">
              {/* What the subject agreed to last time — the summary should say
                  whether they did it — and where the next goals are set while
                  the summary is being written. */}
              <SubjectGoalsPanel
                userId={userId}
                canAssign
                heading={t('perfReview.goals.fromLastReview')}
              />
              {!entries.length && (
                <Center py="lg">
                  <Text c="dimmed">{lockedText ?? t('perfReview.noFeedbackYet')}</Text>
                </Center>
              )}
              {entries.length > 0 && (
                <OverallScoreCard
                  questions={blockOrder.flatMap((k) => answersByBlock.get(k) || [])}
                  t={t}
                />
              )}
              {entries.length > 0 &&
                blockOrder.map((blockKey) => (
                  <BlockFeedbackCard
                    key={blockKey}
                    blockKey={blockKey}
                    blockLabel={blockTitle(blockKey)}
                    questions={answersByBlock.get(blockKey) || []}
                    onCopy={() => handleCopyBlock(blockKey)}
                    isCopied={copiedBlock === blockKey}
                    t={t}
                  />
                ))}
            </Stack>
          </ScrollArea>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
            <Group gap="xs">
              <Text fw={600}>{t('perfReview.summaryByBlock')}</Text>
              {isPublished ? (
                <Badge variant="light" color="teal" leftSection={<IconCheck size={12} />}>
                  {t('perfReview.statusPublished')}
                </Badge>
              ) : (
                isShared && (
                  <Badge variant="light" color="blue" leftSection={<IconUsers size={12} />}>
                    {t('perfReview.statusShared')}
                  </Badge>
                )
              )}
            </Group>
            <Group gap="xs">
              {autosaveBadge}
              <Badge variant="light" color="teal">
                {t('perfReview.progressLabel', { filled: filledCount, total: blockOrder.length })}
              </Badge>
            </Group>
          </Group>
          <ScrollArea h="70vh" type="auto">
            <Stack gap="sm">
              {blockOrder.map((blockKey) => (
                <Paper key={blockKey} withBorder p="sm" radius="md">
                  <Text size="sm" fw={500} mb={6}>
                    {blockTitle(blockKey)}
                  </Text>
                  <Textarea
                    autosize
                    minRows={4}
                    maxRows={12}
                    value={comments[blockKey] ?? ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      isDirtyRef.current = true;
                      setComments((s) => ({ ...s, [blockKey]: value }));
                    }}
                    placeholder={t('perfReview.summaryBlockPlaceholder')}
                  />
                </Paper>
              ))}
              <PerformanceReviewOutcomePanel
                roundId={roundId}
                userId={userId}
                initial={initialOutcome}
              />
            </Stack>
          </ScrollArea>
          <Divider my="md" />
          {/* Two-step release: share with the lead/PM first, publish to the subject
              second. Rollbacks live in the overflow menu — they are rare and
              destructive, and four side-by-side buttons wrapped into a mess. */}
          <Group justify="flex-end" gap="xs" wrap="wrap">
            <Tooltip label={t('perfReview.aiDraft')}>
              <ActionIcon
                variant="light"
                color="grape"
                size={36}
                loading={aiDraft.isPending}
                onClick={handleAiDraft}
                aria-label={t('perfReview.aiDraft')}
              >
                <IconSparkles size={18} />
              </ActionIcon>
            </Tooltip>
            <Box style={{ flex: 1 }} />
            <Button
              variant="default"
              loading={saveBlocks.isPending}
              onClick={handleSave}
              disabled={autosaveStatus === 'saved'}
            >
              {t('perfReview.saveDraft')}
            </Button>
            {!isPublished && (
              <Tooltip label={t('perfReview.shareHint')}>
                <Button
                  variant="light"
                  color="blue"
                  leftSection={<IconUsers size={16} />}
                  loading={share.isPending}
                  onClick={handleShare}
                >
                  {isShared ? t('perfReview.reshare') : t('perfReview.share')}
                </Button>
              </Tooltip>
            )}
            <Button
              color="teal"
              leftSection={<IconSend size={16} />}
              loading={publish.isPending}
              onClick={handlePublish}
            >
              {isPublished ? t('perfReview.republish') : t('perfReview.publish')}
            </Button>
            {(isPublished || isShared) && (
              <Menu position="top-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size={36}
                    loading={unpublish.isPending || unshare.isPending}
                    aria-label={t('perfReview.moreActions')}
                  >
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {isPublished && (
                    <Menu.Item
                      color="orange"
                      leftSection={<IconEyeOff size={14} />}
                      onClick={handleUnpublish}
                    >
                      {t('perfReview.unpublish')}
                    </Menu.Item>
                  )}
                  <Menu.Item
                    color="red"
                    leftSection={<IconEyeOff size={14} />}
                    onClick={handleUnshare}
                  >
                    {t('perfReview.unshare')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    </Modal>
  );
};

type BlockQuestions = {
  questionId: string;
  questionMeta?: PerformanceReviewQuestion;
  rows: { respondentLabel: string; value: unknown; anonymous?: boolean }[];
}[];

// Средняя по набору вопросов: усредняем КАЖДУЮ выставленную оценку, а не
// средние по блокам, поэтому блок с восемью ответами весит больше блока с
// двумя. «Не могу оценить» (строка-сентинел) и null дают NaN/0 и отсеиваются.
const scaleStats = (questions: BlockQuestions) => {
  const scaleMeta = questions.find((q) => q.questionMeta?.type === 'scale')?.questionMeta;
  const min = scaleMeta?.scaleMin ?? 1;
  const max = scaleMeta?.scaleMax ?? 5;
  const values = questions
    .filter((q) => q.questionMeta?.type === 'scale')
    .flatMap((q) => q.rows.map((r) => (typeof r.value === 'number' ? r.value : Number(r.value))))
    .filter((n) => !isNaN(n) && n >= min);
  return {
    min,
    max,
    count: values.length,
    mean: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null,
  };
};

// Плашка «оценка · подпись» — одна и та же вёрстка у блочной средней и у итога,
// иначе два числа на одном экране читались бы как разные сущности.
const ScoreBadge: React.FC<{
  mean: number;
  min: number;
  max: number;
  t: TFunction;
  size?: 'md' | 'lg';
}> = ({ mean, min, max, t, size = 'md' }) => {
  const defaults = t('perfReview.expectationScale', { returnObjects: true });
  const expLabels = Array.isArray(defaults) ? (defaults as string[]) : undefined;
  // Через scoreExpectation, а не Math.round: страница результатов подписывает
  // средние тем же band'ом, и итоговая оценка теперь показывается на обоих
  // экранах — простое округление дало бы одному числу два разных вердикта.
  const meta = scoreExpectation(mean, expLabels, min, max);
  return (
    <Badge
      variant="light"
      color={meta.color}
      radius="sm"
      size={size}
      style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
    >
      {mean.toFixed(1)}/{max}
      {meta.label ? ` · ${meta.label}` : ''}
    </Badge>
  );
};

// Итог по всему ревью — сумма всех шкальных ответов из всех блоков. Стоит над
// блоками: менеджер видит вывод раньше, чем начинает листать частности.
const OverallScoreCard: React.FC<{ questions: BlockQuestions; t: TFunction }> = ({
  questions,
  t,
}) => {
  const { mean, count, min, max } = scaleStats(questions);
  if (mean == null) return null;
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="sm">
            {t('perfReview.overallScore')}
          </Text>
          <Text size="xs" c="dimmed">
            {t('perfReview.overallScoreHint', { count })}
          </Text>
        </Box>
        <ScoreBadge mean={mean} min={min} max={max} t={t} size="lg" />
      </Group>
    </Paper>
  );
};

interface BlockFeedbackCardProps {
  blockKey: string;
  blockLabel: string;
  questions: BlockQuestions;
  onCopy: () => void;
  isCopied: boolean;
  t: TFunction;
}

const BlockFeedbackCard: React.FC<BlockFeedbackCardProps> = ({
  blockLabel,
  questions,
  onCopy,
  isCopied,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const totalRows = questions.reduce((acc, q) => acc + q.rows.length, 0);

  // Aggregate rating for the block: mean of the scored scale answers (cannot-
  // assess and non-numeric are excluded), shown with the expectation label so
  // the admin sees at a glance whether the block met expectations.
  const { mean: scaleMean, min: scaleMin, max: scaleMax } = scaleStats(questions);

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb={isOpen ? 'sm' : 0} wrap="nowrap">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <ActionIcon variant="subtle" onClick={() => setIsOpen((v) => !v)}>
            {isOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </ActionIcon>
          <Text fw={600} size="sm" truncate>
            {blockLabel}
          </Text>
          <Badge variant="light" color="gray" size="xs">
            {totalRows}
          </Badge>
        </Group>
        <Tooltip label={t('perfReview.copyBlockTooltip')}>
          <Button
            size="xs"
            variant={isCopied ? 'filled' : 'light'}
            color={isCopied ? 'teal' : 'blue'}
            leftSection={isCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            onClick={onCopy}
            disabled={!totalRows}
          >
            {isCopied ? t('perfReview.copied') : t('perfReview.copy')}
          </Button>
        </Tooltip>
      </Group>
      {isOpen && (
        <Stack gap="sm">
          {scaleMean != null && (
            <Group
              gap="xs"
              align="center"
              wrap="nowrap"
              style={{
                background: 'var(--mantine-color-default-hover)',
                borderRadius: 10,
                padding: '8px 12px',
              }}
            >
              <Text
                size="xs"
                fw={700}
                c="dimmed"
                tt="uppercase"
                style={{ letterSpacing: '0.05em' }}
              >
                {t('perfReview.blockAvgLabel')}
              </Text>
              <ScoreBadge mean={scaleMean} min={scaleMin} max={scaleMax} t={t} />
            </Group>
          )}
          {questions.length === 0 && (
            <Text size="xs" c="dimmed" fs="italic">
              {t('perfReview.noQuestionsInBlock')}
            </Text>
          )}
          {questions.map((qBlock) => {
            const qmeta = qBlock.questionMeta;
            return (
              <Box
                key={qBlock.questionId}
                style={{
                  background: 'var(--mantine-color-default-hover)',
                  borderRadius: 10,
                  padding: '10px 12px',
                }}
              >
                <Group gap={8} mb={8} wrap="nowrap" align="center">
                  <Text
                    size="xs"
                    fw={700}
                    c="dimmed"
                    tt="uppercase"
                    style={{ letterSpacing: '0.05em', flex: 1, minWidth: 0 }}
                  >
                    {qmeta?.text || qBlock.questionId}
                  </Text>
                  {qmeta?.anonymous && qmeta?.type !== 'scale' && (
                    <Tooltip label={t('perfReview.anonymousAdminTooltip')} withArrow>
                      <Badge
                        size="xs"
                        variant="light"
                        color="gray"
                        radius="sm"
                        style={{ flexShrink: 0 }}
                      >
                        {t('perfReview.anonymousForPeers')}
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                {qBlock.rows.length === 0 ? (
                  <Text size="xs" c="dimmed" fs="italic">
                    {t('perfReview.noAnswersYet')}
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {qBlock.rows.map((row, ri) => (
                      // Admin summary editor always shows the author, even for
                      // questions anonymous to peers. Name on its own line above
                      // the answer so long comments don't leave a lonely badge.
                      <Box
                        key={ri}
                        style={{
                          borderLeft: '2px solid var(--mantine-color-default-border)',
                          paddingLeft: 12,
                        }}
                      >
                        <Group gap={6} mb={4} align="center" wrap="nowrap">
                          <Text size="xs" fw={600}>
                            {row.respondentLabel}
                          </Text>
                          {/* Anon/signed badge only for text answers — scale
                              scores aren't surfaced to the subject, so tagging
                              their anonymity is noise. */}
                          {qmeta?.type !== 'scale' && (
                            <Tooltip
                              label={
                                row.anonymous
                                  ? t('perfReview.submittedAnonTooltip')
                                  : t('perfReview.submittedSignedTooltip')
                              }
                              withArrow
                            >
                              <Badge
                                size="xs"
                                variant="light"
                                radius="sm"
                                color={row.anonymous ? 'gray' : 'teal'}
                                style={{ flexShrink: 0 }}
                              >
                                {row.anonymous
                                  ? t('perfReview.submittedAnon')
                                  : t('perfReview.submittedSigned')}
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        <div>{renderAnswerValue(qmeta, row.value, t)}</div>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};
